import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { saveScan } from '../utils/historyManager';
import AndroidChecker from '../components/AndroidChecker';
import WindowsChecker from '../components/WindowsChecker';
import ResultDisplay from '../components/ResultDisplay';

const MalwareTools = () => {
    const { t } = useTranslation();
    const { theme, darkMode } = useTheme();
    const [activeTab, setActiveTab] = useState('android');
    const [tabResults, setTabResults] = useState({ android: { result: null }, windows: { result: null } });
    const [loading, setLoading] = useState(false);

    const MALWARE_TABS = [
        { id: 'android', label: t('tools.tabs.android'), icon: 'bx-mobile-alt', desc: t('tools.desc.android'), accentColor: '#22c55e', accentRgb: '34,197,94', stats: ['Permissions', 'Intent Filters', 'API Calls', 'Native Libs'] },
        { id: 'windows', label: t('tools.tabs.windows'), icon: 'bx-desktop', desc: t('tools.desc.windows'), accentColor: '#06b6d4', accentRgb: '6,182,212', stats: ['PE Headers', 'Import Table', 'Entropy', 'Sections'], comingSoon: true },
    ];

    const handleResult = (data) => {
        setTabResults(prev => ({ ...prev, [activeTab]: { result: data } }));

        saveScan({
            type: activeTab,
            target: data.filename || (activeTab === 'android' ? 'APK Analysis' : 'Windows PE Analysis'),
            result: data.prediction || (data.malicious ? 'malware' : 'legitimate'),
            confidence: data.confidence || 0,
            icon: activeTab === 'android' ? 'bx-mobile-alt' : 'bx-desktop'
        });
    };

    const activeInfo = MALWARE_TABS.find(t => t.id === activeTab);
    const currentResult = tabResults[activeTab]?.result;

    return (
        <div className="min-h-screen pt-24 pb-20 transition-colors duration-300 relative" style={{ background: theme.bg }}>
            {/* Background Grid */}
            <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: `linear-gradient(${theme.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${theme.gridLine} 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

            {darkMode && (
                <div className="fixed top-[20%] -right-[200px] w-[600px] h-[600px] rounded-full blur-[150px] pointer-events-none opacity-10" style={{ background: 'radial-gradient(circle, #ef4444 0%, transparent 70%)' }} />
            )}

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-10"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-1 h-6 bg-red-500 rounded-full" />
                        <span className="text-red-500 text-xs font-bold tracking-[0.2em] uppercase">Malware Analysis Engine</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight" style={{ color: theme.text }}>
                        Malware <span className="text-red-500">Detection</span> Tools
                    </h1>
                    <p className="text-sm md:text-base mt-3 max-w-2xl opacity-70" style={{ color: theme.text }}>
                        {t('tools.malware_subtitle')}
                    </p>
                </motion.div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    {[
                        { icon: 'bx-mobile-alt', label: 'APK Analyzed', value: '12,847', rgb: '34,197,94' },
                        { icon: 'bx-desktop', label: 'PE Analyzed', value: '8,293', rgb: '6,182,212' },
                        { icon: 'bx-shield-x', label: 'Threats Found', value: '4,521', rgb: '239,68,68' },
                        { icon: 'bx-bullseye', label: 'ML Accuracy', value: '98.7%', rgb: '245,158,11' },
                    ].map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="p-5 rounded-2xl border backdrop-blur-sm flex items-center gap-4 group hover:scale-[1.02] transition-transform"
                            style={{ background: theme.bgCard, borderColor: theme.border }}
                        >
                            <div className="w-12 h-12 flex items-center justify-center rounded-xl transition-colors duration-300"
                                style={{ background: `rgba(${s.rgb}, 0.1)`, color: `rgb(${s.rgb})` }}>
                                <i className={`bx ${s.icon} text-2xl`} />
                            </div>
                            <div>
                                <div className="text-xl font-black" style={{ color: theme.text }}>{s.value}</div>
                                <div className="text-[10px] font-black uppercase tracking-widest opacity-40" style={{ color: theme.text }}>{s.label}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left Sidebar: Tabs */}
                    <div className="lg:col-span-4 space-y-4">
                        {MALWARE_TABS.map(tab => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full p-6 rounded-2xl border transition-all duration-300 text-left flex items-start gap-4 relative overflow-hidden group
                                        ${isActive ? 'shadow-2xl translate-x-2' : 'hover:bg-opacity-5'}`}
                                    style={{
                                        background: isActive ? `rgba(${tab.accentRgb}, 0.08)` : theme.bgCard,
                                        borderColor: isActive ? tab.accentColor : theme.border
                                    }}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeGlow"
                                            className="absolute inset-0 pointer-events-none opacity-20"
                                            style={{ background: `radial-gradient(circle at center, ${tab.accentColor} 0%, transparent 70%)` }}
                                        />
                                    )}
                                    <div className={`w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-xl border transition-all duration-300
                                        ${isActive ? 'scale-110 shadow-lg bg-opacity-20' : 'group-hover:scale-105 opacity-50'}`}
                                        style={{
                                            background: isActive ? tab.accentColor : theme.badgeBg,
                                            borderColor: isActive ? 'transparent' : theme.border,
                                            color: isActive ? tab.accentColor : theme.textDim
                                        }}
                                    >
                                        <i className={`bx ${tab.icon} text-3xl`} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="font-black text-lg tracking-tight" style={{ color: isActive ? theme.text : theme.textMuted }}>
                                                {tab.label}
                                            </div>
                                            {tab.comingSoon && (
                                                <span className="px-2 py-0.5 rounded text-[9px] uppercase font-black bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 tracking-wider">
                                                    Coming Soon
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs font-bold opacity-60 mb-3" style={{ color: theme.text }}>
                                            {tab.desc}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {tab.stats.map(s => (
                                                <span key={s} className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded backdrop-blur-md"
                                                    style={{
                                                        background: isActive ? `rgba(${tab.accentRgb}, 0.15)` : theme.badgeBg,
                                                        color: isActive ? tab.accentColor : theme.textFaint,
                                                        border: `1px solid ${isActive ? `rgba(${tab.accentRgb}, 0.2)` : 'transparent'}`
                                                    }}>
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Right Content: Checker */}
                    <div className="lg:col-span-8 flex flex-col">
                        <motion.div
                            layout
                            className="flex-1 rounded-3xl border shadow-2xl overflow-hidden backdrop-blur-xl"
                            style={{
                                background: theme.bgCard2,
                                borderColor: `rgba(${activeInfo?.accentRgb}, 0.3)`
                            }}
                        >
                            <div className="px-8 py-4 flex items-center justify-between border-b bg-opacity-5" style={{ background: activeInfo?.accentColor, borderColor: `rgba(${activeInfo?.accentRgb}, 0.15)` }}>
                                <div className="flex items-center gap-3">
                                    <i className={`bx ${activeInfo?.icon}`} style={{ color: activeInfo?.accentColor }} />
                                    <span className="text-xs font-black tracking-widest uppercase" style={{ color: activeInfo?.accentColor }}>
                                        {activeInfo?.label} STATIC ANALYSIS
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    <span className="text-[10px] font-black text-green-500 uppercase tracking-widest">{t('tools.model_loaded')}</span>
                                </div>
                            </div>

                            <div className="p-10">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeTab}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {activeTab === 'android' && <AndroidChecker setLoading={setLoading} onResult={handleResult} />}
                                        {activeTab === 'windows' && (
                                            <div className="text-center py-16">
                                                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-cyan-500/10 text-cyan-500 mb-6 border border-cyan-500/20 shadow-lg shadow-cyan-500/10">
                                                    <i className="bx bx-time-five text-5xl" />
                                                </div>
                                                <h3 className="text-2xl font-black mb-3 tracking-tight" style={{ color: theme.text }}>{t('tools.tabs.windows')} Analysis</h3>
                                                <p className="text-sm opacity-60 max-w-sm mx-auto leading-relaxed" style={{ color: theme.text }}>
                                                    This feature is currently under development. Stay tuned for advanced static and dynamic ML analysis of Windows PE files!
                                                </p>
                                            </div>
                                        )}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </motion.div>

                        {/* Analysis Feedback Area */}
                        <AnimatePresence>
                            {loading && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-4 p-6 rounded-2xl border overflow-hidden"
                                    style={{ background: theme.bgCard, borderColor: `rgba(${activeInfo?.accentRgb}, 0.3)` }}
                                >
                                    <div className="flex items-center gap-4">
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                            className="text-2xl"
                                            style={{ color: activeInfo?.accentColor }}
                                        >
                                            <i className="bx bx-loader-alt" />
                                        </motion.div>
                                        <span className="font-bold text-sm tracking-tight" style={{ color: theme.text }}>
                                            {t('tools.analyzing')}
                                        </span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Result Area */}
                        <AnimatePresence>
                            {!loading && currentResult && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-6"
                                >
                                    <ResultDisplay result={currentResult} />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Technical Overview Section */}
                {!loading && !currentResult && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}
                        className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
                    >
                        {[
                            { icon: 'bx-search-alt-2', title: 'Static Analysis', desc: 'Analyzes file structure and signatures without execution - 100% safe sandbox-free protocol.' },
                            { icon: 'bx-dna', title: 'Feature Extraction', desc: 'Extracts 100+ critical features including PE headers, permissions, API call graphs, and entropy.' },
                            { icon: 'bx-bar-chart-alt-2', title: 'ML Classification', desc: 'Uses weighted Random Forest + Gradient Boosting ensembles for high detection precision.' },
                        ].map((c, i) => (
                            <div key={i} className="p-8 rounded-2xl border transition-all duration-300 hover:shadow-xl group backdrop-blur-sm"
                                style={{ background: theme.bgCard, borderColor: theme.border }}>
                                <i className={`bx ${c.icon} text-3xl mb-6 transition-transform group-hover:scale-110`} style={{ color: '#ef4444' }} />
                                <h3 className="font-black text-base mb-3 tracking-tight" style={{ color: theme.text }}>{c.title}</h3>
                                <p className="text-xs leading-relaxed opacity-60 font-medium" style={{ color: theme.text }}>{c.desc}</p>
                            </div>
                        ))}
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default MalwareTools;
